import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  TruckIcon, Boxes, PackageCheck, Wallet, Eye, EyeOff, MapPin, IndianRupee,
  Headset, PackageSearch, ArrowRight, Sparkles, Radar, ShieldCheck, ExternalLink,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: Boxes,
    title: "B2B freight & appointment delivery",
    description: "LTL to marketplaces and distributors — scheduled pickups, LR copies, POD tracking.",
  },
  {
    icon: PackageCheck,
    title: "B2C last-mile shipping",
    description: "Ship D2C orders across India — COD & prepaid, zone-based rates, doorstep tracking.",
  },
  {
    icon: Wallet,
    title: "One wallet, transparent billing",
    description: "Prepaid wallet, courier-wise rate cards, COD remittance and invoices in one place.",
  },
];

// Truthful capability statements (no invented metrics).
const STATS = [
  { icon: MapPin, label: "Pan-India coverage" },
  { icon: IndianRupee, label: "COD & Prepaid" },
  { icon: Radar, label: "Real-time tracking" },
  { icon: Headset, label: "Dedicated support" },
];

const AuthPage = ({ setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Login successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // Opens the public tracking page in its own tab (no auth required).
  const openTracking = () => window.open("/track", "_blank", "noopener,noreferrer");
  // Opens the Edgistify corporate site.
  const openEdgistify = () => window.open("https://www.edgistify.com/", "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50">
      {/* Top bar: brand + the two public CTAs (Track + Sign up) */}
      <header className="px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-brand shadow-sm">
            <TruckIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 leading-none">EdgeLMS</h1>
            <p className="text-xs font-medium text-slate-500 tracking-wide">by Edgistify</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openTracking} data-testid="track-order-button">
            <PackageSearch className="w-4 h-4 mr-2" /> Track Your Order
          </Button>
          <Button size="sm" className="hidden sm:inline-flex" onClick={() => navigate("/signup")} data-testid="header-signup-button">
            <Sparkles className="w-4 h-4 mr-2" /> Sign Up for Free
          </Button>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 items-center gap-8 lg:gap-12">
          {/* Left — branded hero panel (desktop / tablet landscape) */}
          <div className="hidden lg:block">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand via-teal-600 to-emerald-600 text-white p-9 shadow-2xl">
              {/* soft decorative glows */}
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
              <div className="relative space-y-7">
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider bg-white/15 rounded-full px-3 py-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Edgistify Last Mile Management System
                </span>
                <div className="space-y-3">
                  <h2 className="text-3xl xl:text-4xl font-heading font-bold leading-tight">
                    Ship B2B freight and B2C parcels from one platform.
                  </h2>
                  <p className="text-base text-white/85 leading-relaxed max-w-md">
                    Move pallets to marketplaces or parcels to doorsteps — manage every shipment,
                    wallet and invoice from a single portal.
                  </p>
                </div>

                <div className="space-y-3">
                  {CAPABILITIES.map(({ icon: Icon, title, description }) => (
                    <div key={title} className="flex items-start gap-3.5 rounded-2xl bg-white/10 backdrop-blur-sm p-3.5">
                      <div className="p-2 rounded-xl bg-white/15 shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold leading-tight">{title}</h3>
                        <p className="text-sm text-white/80 leading-snug mt-0.5">{description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  {STATS.map(({ icon: Icon, label }) => (
                    <span key={label} className="inline-flex items-center gap-1.5 text-xs font-medium bg-white/15 rounded-full px-3 py-1.5">
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </span>
                  ))}
                </div>
                <button
                  onClick={openEdgistify}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white"
                  data-testid="auth-explore-edgistify"
                >
                  Explore Edgistify <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Right — login card + the two CTAs */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
            {/* Condensed pitch for phone / tablet portrait (hero is desktop-only) */}
            <div className="lg:hidden mb-6 text-center space-y-3">
              <p className="text-sm text-slate-600 px-4">
                B2B freight & B2C last-mile shipping, one portal.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {STATS.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-white/70 rounded-full px-3 py-1.5">
                    <Icon className="w-3.5 h-3.5 text-brand" /> {label}
                  </span>
                ))}
              </div>
            </div>

            <Card className="shadow-2xl border-0">
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-xl font-semibold">Welcome back</CardTitle>
                <CardDescription>Sign in to your seller or admin portal</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      autoComplete="email"
                      className="text-base h-11"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        className="pr-10 text-base h-11"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" data-testid="login-submit-button" className="w-full h-11" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>

                {/* Divider + prominent free-signup CTA */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-slate-400">New to EdgeLMS?</span></div>
                </div>
                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => navigate("/signup")}
                  data-testid="auth-create-account-link"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Sign Up for Free
                </Button>

                <button
                  type="button"
                  onClick={openTracking}
                  className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-brand hover:underline"
                  data-testid="track-order-link"
                >
                  <PackageSearch className="w-4 h-4" /> Track your order
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <footer className="py-4 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} OptiSupply Chain Solution Pvt. Ltd. · EdgeLMS ·{" "}
        <button onClick={openEdgistify} className="hover:text-brand underline" data-testid="auth-footer-edgistify">edgistify.com</button>
      </footer>
    </div>
  );
};

export default AuthPage;
